'use client';

import { c as _c } from "react/compiler-runtime";
import { InterfaceChainUtils, useLocalStorageState } from '@morpho-org/dapp-core';
import { Avatar } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { SCREENING_BLOCKED_MESSAGE } from '@repo/address-screening/client';
import { Button, StatusToast } from '@repo/ui';
import { Alert, ArrowDiagonalUp, ArrowRight, Chevron } from '@repo/ui/icons';
import { useUserAccount } from '@repo/web3';
import Link from 'next/link';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary/SectionErrorBoundary';
import { getFixedRateAppUrl } from '@/config/external-urls';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getVaultUrl, VaultPageTabValues } from '@/helpers/routing';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useTrackDeprecatedVaultPromptViewed } from '@/lib/analytics/client/hooks/useTrackDeprecatedVaultPromptViewed';
import { trackDeprecatedVaultPromptClicked, trackDeprecatedVaultPromptToggled } from '@/lib/analytics/client/trackers/deprecated-vault';
import { trackFixedRateAppClicked, trackFixedRateBannerDismissed } from '@/lib/analytics/client/trackers/fixed-rate';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { useNotifications } from './useNotifications';
import { WrapLegacyMorphoDialog } from './WrapLegacyMorphoDialog';
type WalletStatusToastId = 'legacyMorpho' | 'screeningBlocked';
type StatusToastId = WalletStatusToastId | 'uk' | 'embedder' | 'fixedRate' | `deprecatedVault-${string}`;
type WalletStatusToastVersions = Record<WalletStatusToastId, number>;
interface StatusToastAction {
  label: string;
  ariaLabel: string;
  onClick?: () => void;
  /** When set, the action renders as a `next/link` instead of a plain button. */
  href?: string;
  /** With `href`, render an external anchor (new tab + rel) instead of a `next/link`. */
  external?: boolean;
  iconRight?: ReactElement;
}
interface StatusToastDefinition {
  id: StatusToastId;
  title: string;
  description: ReactNode;
  icon: ReactNode;
  action?: StatusToastAction;
}
const EMPTY_VERSIONS: WalletStatusToastVersions = {
  legacyMorpho: 0,
  screeningBlocked: 0
};
const UK_DISCLAIMER_ICON = <Alert className="text-destructive-foreground h-4 w-4" data-testid="uk-disclaimer-icon" />;
const UK_TOAST: StatusToastDefinition = {
  id: 'uk',
  title: 'UK residents',
  description: 'Morpho Association (ADDMO) is not authorized or regulated by the FCA. UK regulatory protections will not apply.',
  icon: UK_DISCLAIMER_ICON
};

// First-visit promo for the sibling Fixed Rate app (VRM-5344). Reuses the legacy
// banner's dismissal key so users who already dismissed the banner aren't re-notified.
const FIXED_RATE_DISMISS_KEY = 'fixed-rate-intro-banner-dismissed';

// Copy approved by product (Sandro).
const FIXED_RATE_TOAST: StatusToastDefinition = {
  id: 'fixedRate',
  title: 'New: Fixed Rate Markets',
  description: 'Lock in a Fixed rate when Lending or Borrowing on Morpho’s new Fixed Rate Markets.',
  icon: <Avatar src="https://cdn.morpho.org/assets/logos/morpho.svg" size="small" rounded />,
  action: {
    label: 'Explore Fixed Rate',
    ariaLabel: 'Explore the Fixed Rate app',
    href: getFixedRateAppUrl('banner'),
    external: true,
    iconRight: <ArrowDiagonalUp />,
    onClick: () => trackFixedRateAppClicked({
      source: 'banner'
    })
  }
};
interface StatusToastArticleProps {
  toast: StatusToastDefinition;
  /** Omit for a toast the user can't dismiss. */
  onDismiss?: () => void;
  /** DOM id on the card, for a disclosure trigger's `aria-controls`. */
  id?: string;
  /**
   * `off` when the card is a disclosure panel the user just opened: the stack is a
   * polite live region, so without it the whole card is read out as a new alert.
   */
  'aria-live'?: 'off';
}
const StatusToastArticle = t0 => {
  const $ = _c(11);
  const {
    toast,
    onDismiss,
    id: elementId,
    "aria-live": ariaLive
  } = t0;
  const {
    id,
    title,
    description,
    icon,
    action
  } = toast;
  let t1;
  if ($[0] !== action) {
    t1 = action ? <StatusToastActionButton action={action} /> : undefined;
    $[0] = action;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const actionButton = t1;
  let t2;
  if ($[2] !== actionButton || $[3] !== ariaLive || $[4] !== description || $[5] !== elementId || $[6] !== icon || $[7] !== id || $[8] !== onDismiss || $[9] !== title) {
    t2 = <StatusToast id={elementId} aria-live={ariaLive} toastId={id} title={title} description={description} icon={icon} action={actionButton} onDismiss={onDismiss} />;
    $[2] = actionButton;
    $[3] = ariaLive;
    $[4] = description;
    $[5] = elementId;
    $[6] = icon;
    $[7] = id;
    $[8] = onDismiss;
    $[9] = title;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
function StatusToastActionButton(t0) {
  const $ = _c(20);
  const {
    action
  } = t0;
  if (action.href && action.external) {
    let t1;
    if ($[0] !== action.href || $[1] !== action.iconRight || $[2] !== action.label || $[3] !== action.onClick) {
      t1 = <ExternalLink href={action.href} onClick={action.onClick}>{action.label}{action.iconRight}</ExternalLink>;
      $[0] = action.href;
      $[1] = action.iconRight;
      $[2] = action.label;
      $[3] = action.onClick;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== action.ariaLabel || $[6] !== t1) {
      t2 = <Button asChild={true} variant="secondary" size="sm" aria-label={action.ariaLabel}>{t1}</Button>;
      $[5] = action.ariaLabel;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (action.href) {
    let t1;
    if ($[8] !== action.href || $[9] !== action.iconRight || $[10] !== action.label || $[11] !== action.onClick) {
      t1 = <Link href={action.href} onClick={action.onClick}>{action.label}{action.iconRight}</Link>;
      $[8] = action.href;
      $[9] = action.iconRight;
      $[10] = action.label;
      $[11] = action.onClick;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    let t2;
    if ($[13] !== action.ariaLabel || $[14] !== t1) {
      t2 = <Button asChild={true} variant="secondary" size="sm" aria-label={action.ariaLabel}>{t1}</Button>;
      $[13] = action.ariaLabel;
      $[14] = t1;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  let t1;
  if ($[16] !== action.ariaLabel || $[17] !== action.label || $[18] !== action.onClick) {
    t1 = <Button variant="secondary" size="sm" aria-label={action.ariaLabel} onClick={action.onClick}>{action.label}</Button>;
    $[16] = action.ariaLabel;
    $[17] = action.label;
    $[18] = action.onClick;
    $[19] = t1;
  } else {
    t1 = $[19];
  }
  return t1;
}

/**
 * Deprecated-vault prompt. Desktop renders the toast as-is. Mobile starts it collapsed
 * as a one-line chip, because the full card in the fixed bottom stack covers page
 * content on a phone; tapping the chip opens the card and tapping it again closes it.
 *
 * Still undismissable: collapsing only hides the copy for this render, and nothing is
 * persisted. Withdrawing remains the only way to clear the prompt.
 */
const DeprecatedVaultToast = t0 => {
  const $ = _c(24);
  const {
    toast,
    isMobile,
    onToggle
  } = t0;
  const [isExpanded, setIsExpanded] = useState(false);
  if (!isMobile) {
    let t1;
    if ($[0] !== toast) {
      t1 = <StatusToastArticle toast={toast} />;
      $[0] = toast;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const panelId = `status-toast-${toast.id}-panel`;
  const t1 = `status-toast-toggle-${toast.id}`;
  const t2 = isExpanded ? panelId : undefined;
  let t3;
  if ($[2] !== isExpanded || $[3] !== onToggle) {
    t3 = () => {
      const nextExpanded = !isExpanded;
      setIsExpanded(nextExpanded);
      onToggle(nextExpanded);
    };
    $[2] = isExpanded;
    $[3] = onToggle;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Alert aria-hidden={true} className="text-destructive-foreground size-4" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `${toast.title} deprecated`;
  let t6;
  if ($[6] !== t5) {
    t6 = <span className="truncate">{t5}</span>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isExpanded ? "text-secondary-foreground rotate-0 transition-transform" : "text-secondary-foreground -rotate-90 transition-transform";
  let t8;
  if ($[8] !== t7) {
    t8 = <Chevron aria-hidden={true} className={t7} />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isExpanded || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t6 || $[15] !== t8) {
    t9 = <Button variant="secondary" size="md" className="pointer-events-auto max-w-[min(360px,calc(100vw-32px))] self-start" data-testid={t1} aria-controls={t2} aria-expanded={isExpanded} onClick={t3}>{t4}{t6}{t8}</Button>;
    $[10] = isExpanded;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isExpanded || $[18] !== panelId || $[19] !== toast) {
    t10 = isExpanded ? <StatusToastArticle toast={toast} id={panelId} aria-live="off" /> : null;
    $[17] = isExpanded;
    $[18] = panelId;
    $[19] = toast;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="gap-2xs flex flex-col">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
interface StatusToastsInnerProps {
  showUk: boolean;
  onUkDismiss: () => void;
  showEmbedder: boolean;
  embedderBanner: string | undefined;
  onEmbedderDismiss: () => void;
  showFixedRate: boolean;
  onFixedRateDismiss: () => void;
  screeningBlocked: boolean;
  screeningBlockedCount: number;
}
const StatusToastsInner = ({
  showUk,
  onUkDismiss,
  showEmbedder,
  embedderBanner,
  onEmbedderDismiss,
  showFixedRate,
  onFixedRateDismiss,
  screeningBlocked,
  screeningBlockedCount
}: StatusToastsInnerProps) => {
  const {
    toggleDialog
  } = useDialogContext();
  const isMobile = useIsMobile();
  const {
    hasLegacyMorpho,
    deprecatedVaultPositions
  } = useNotifications();
  // Mobile renders the prompt as a collapsed chip, so tag the impression with what the
  // user actually saw.
  useTrackDeprecatedVaultPromptViewed(deprecatedVaultPositions, isMobile ? 'chip' : 'card');

  // Real-signal connection gating happens in `useNotifications` (legacyMorphoHolding
  // is null without a connection); the debug override bypasses that intentionally.
  const activeWalletToastIds: WalletStatusToastId[] = [];
  if (hasLegacyMorpho) activeWalletToastIds.push('legacyMorpho');
  if (screeningBlocked) activeWalletToastIds.push('screeningBlocked');

  // Initialize already-active toasts at version 1 so a dismiss on first paint
  // (before the activation-bump effect runs) can't be re-overridden into "visible".
  const [activationVersions, setActivationVersions] = useState<WalletStatusToastVersions>(() => {
    const initial: WalletStatusToastVersions = {
      ...EMPTY_VERSIONS
    };
    for (const id of activeWalletToastIds) initial[id] = 1;
    return initial;
  });
  const [dismissedToastVersions, setDismissedToastVersions] = useState<Partial<WalletStatusToastVersions>>({});
  const previousActiveToastIdsRef = useRef<Set<WalletStatusToastId>>(new Set(activeWalletToastIds));
  const previousScreeningBlockedCountRef = useRef(screeningBlockedCount);
  const activationKey = activeWalletToastIds.join('|');

  // Bump activation version when a notification transitions from inactive to
  // active, so a dismiss + re-activate cycle surfaces the toast again.
  useEffect(() => {
    const previous = previousActiveToastIdsRef.current;
    const next = new Set(activeWalletToastIds);
    const newlyActive = activeWalletToastIds.filter(id => !previous.has(id));
    const screeningBlockedReactivated = screeningBlocked && screeningBlockedCount > previousScreeningBlockedCountRef.current;
    if (newlyActive.length > 0 || screeningBlockedReactivated) {
      setActivationVersions(prev => {
        const update: WalletStatusToastVersions = {
          ...prev
        };
        for (const id of newlyActive) update[id] = prev[id] + 1;
        if (screeningBlockedReactivated) update.screeningBlocked = prev.screeningBlocked + 1;
        return update;
      });
    }
    previousActiveToastIdsRef.current = next;
    previousScreeningBlockedCountRef.current = screeningBlockedCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on activationKey, the stable join('|') of activeWalletToastIds; the array itself is a fresh ref each render, so depending on it would re-run every render.
  }, [activationKey, screeningBlocked, screeningBlockedCount]);
  const dismissWalletToast = (toastId: WalletStatusToastId) => {
    setDismissedToastVersions(current => {
      const version = activationVersions[toastId];
      if (current[toastId] === version) return current;
      return {
        ...current,
        [toastId]: version
      };
    });
  };
  const walletDefinitionById: Record<WalletStatusToastId, StatusToastDefinition> = {
    legacyMorpho: {
      id: 'legacyMorpho',
      title: 'Legacy MORPHO Holder',
      description: 'MORPHO is now transferable. Wrap your legacy MORPHO tokens to the latest version.',
      icon: <Avatar size="small" src="https://cdn.morpho.org/assets/logos/morpho.svg" />,
      action: {
        label: 'Wrap',
        ariaLabel: 'Wrap legacy MORPHO tokens',
        onClick: () => toggleDialog(<WrapLegacyMorphoDialog />, {
          closable: true
        })
      }
    },
    screeningBlocked: {
      id: 'screeningBlocked',
      title: 'Wallet disconnected',
      description: SCREENING_BLOCKED_MESSAGE,
      icon: <Alert className="text-destructive-foreground h-4 w-4" />
    }
  };
  const visibleWalletToasts = activeWalletToastIds.filter(id => dismissedToastVersions[id] !== activationVersions[id]).map(id => walletDefinitionById[id]);

  // Undismissable by design: the only way to clear it is to withdraw, which drops the
  // redeemable value below the vault's floor and removes it from `deprecatedVaultPositions`.
  const deprecatedVaultPrompts: {
    toast: StatusToastDefinition;
    onToggle: (isExpanded: boolean) => void;
  }[] = deprecatedVaultPositions
  // `getIdentifierById` throws on a chain the interface doesn't list, and this runs
  // during render inside a boundary whose fallback never resets: one bad entry would
  // take out the whole toast stack, screening and FCA notices included.
  .filter(vault => InterfaceChainUtils.tryGetChain(vault.chainId)).map(vault => ({
    onToggle: (isExpanded: boolean) => trackDeprecatedVaultPromptToggled({
      chainId: vault.chainId,
      vaultAddress: vault.address,
      isExpanded
    }),
    toast: {
      id: `deprecatedVault-${vault.address}`,
      title: vault.name,
      description: 'This vault is deprecated, please withdraw your funds.',
      icon: <Alert className="text-destructive-foreground h-4 w-4" />,
      action: {
        label: 'Withdraw',
        ariaLabel: `Withdraw from ${vault.name}`,
        href: getVaultUrl({
          identifier: InterfaceChainUtils.getIdentifierById(vault.chainId),
          vaultAddress: vault.address,
          vaultName: vault.name,
          mobileTab: VaultPageTabValues.MyPosition
        }),
        iconRight: <ArrowRight />,
        onClick: () => trackDeprecatedVaultPromptClicked({
          chainId: vault.chainId,
          vaultAddress: vault.address
        })
      }
    }
  }));
  const embedderToast: StatusToastDefinition | null = showEmbedder && embedderBanner ? {
    id: 'embedder',
    // Risk caution (matches the UK FCA notice styling): the user is on a
    // third-party surface, not app.morpho.org directly.
    title: 'Third-party app',
    description: embedderBanner,
    icon: <Alert className="text-destructive-foreground h-4 w-4" data-testid="embedder-banner-icon" />
  } : null;
  if (!showUk && !embedderToast && !showFixedRate && visibleWalletToasts.length === 0 && deprecatedVaultPrompts.length === 0) return null;
  return <section data-testid="status-toast-stack" aria-label="Notifications" aria-live="polite" aria-relevant="additions" className="gap-sm pointer-events-none fixed bottom-4 left-4 z-60 flex max-w-[calc(100vw-32px)] flex-col">
      {deprecatedVaultPrompts.map(({
      toast,
      onToggle
    }) =>
    // Keyed on the viewport too, so crossing the breakpoint remounts and the chip
    // always starts collapsed. Many phones are wider than 768px in landscape, and a
    // rotation round trip would otherwise bring the card back already open.
    <DeprecatedVaultToast key={`${toast.id}-${isMobile ? 'chip' : 'card'}`} toast={toast} isMobile={isMobile} onToggle={onToggle} />)}
      {showUk ? <StatusToastArticle toast={UK_TOAST} onDismiss={onUkDismiss} /> : null}
      {embedderToast ? <StatusToastArticle toast={embedderToast} onDismiss={onEmbedderDismiss} /> : null}
      {visibleWalletToasts.map(toast => <StatusToastArticle key={toast.id} toast={toast} onDismiss={() => dismissWalletToast(toast.id as WalletStatusToastId)} />)}
      {showFixedRate ? <StatusToastArticle toast={FIXED_RATE_TOAST} onDismiss={onFixedRateDismiss} /> : null}
    </section>;
};

/** @internal */
export const StatusToasts = () => {
  const $ = _c(18);
  const {
    isUkUser,
    embedderBanner
  } = useNotifications();
  const {
    screeningBlocked,
    screeningBlockedCount
  } = useUserAccount();
  const {
    account: t0
  } = useWeb3Context();
  const {
    address,
    isConnected
  } = t0;
  const [dismissedForIsUkUser, setDismissedForIsUkUser] = useState(null);
  const isUkDismissed = dismissedForIsUkUser === isUkUser;
  const showUk = isUkUser && !isUkDismissed;
  let t1;
  if ($[0] !== isUkUser) {
    t1 = () => setDismissedForIsUkUser(isUkUser);
    $[0] = isUkUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleUkDismiss = t1;
  const [dismissedForEmbedderBanner, setDismissedForEmbedderBanner] = useState(null);
  const isEmbedderDismissed = dismissedForEmbedderBanner === embedderBanner;
  const showEmbedder = embedderBanner !== undefined && !isEmbedderDismissed;
  let t2;
  if ($[2] !== embedderBanner) {
    t2 = () => setDismissedForEmbedderBanner(embedderBanner ?? null);
    $[2] = embedderBanner;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleEmbedderDismiss = t2;
  const {
    fixedRateIntroBanner: isFixedRateEnabled
  } = useFeatureFlags();
  const isMobile = useIsMobile();
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultValue: false,
      defaultServerValue: true
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [fixedRateDismissed, setFixedRateDismissed] = useLocalStorageState(FIXED_RATE_DISMISS_KEY, t3);
  const showFixedRate = isFixedRateEnabled && !fixedRateDismissed && !isMobile;
  let t4;
  if ($[5] !== setFixedRateDismissed) {
    t4 = () => {
      setFixedRateDismissed(true);
      trackFixedRateBannerDismissed();
    };
    $[5] = setFixedRateDismissed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleFixedRateDismiss = t4;
  const innerKey = isConnected && address ? address : "disconnected";
  let t5;
  if ($[7] !== embedderBanner || $[8] !== handleEmbedderDismiss || $[9] !== handleFixedRateDismiss || $[10] !== handleUkDismiss || $[11] !== innerKey || $[12] !== screeningBlocked || $[13] !== screeningBlockedCount || $[14] !== showEmbedder || $[15] !== showFixedRate || $[16] !== showUk) {
    t5 = <SectionErrorBoundary fallback={null} section="StatusToasts"><StatusToastsInner key={innerKey} showUk={showUk} onUkDismiss={handleUkDismiss} showEmbedder={showEmbedder} embedderBanner={embedderBanner} onEmbedderDismiss={handleEmbedderDismiss} showFixedRate={showFixedRate} onFixedRateDismiss={handleFixedRateDismiss} screeningBlocked={screeningBlocked} screeningBlockedCount={screeningBlockedCount} /></SectionErrorBoundary>;
    $[7] = embedderBanner;
    $[8] = handleEmbedderDismiss;
    $[9] = handleFixedRateDismiss;
    $[10] = handleUkDismiss;
    $[11] = innerKey;
    $[12] = screeningBlocked;
    $[13] = screeningBlockedCount;
    $[14] = showEmbedder;
    $[15] = showFixedRate;
    $[16] = showUk;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export default StatusToasts;